/**
 * BaseTracker — shared lifecycle for all the MediaPipe wrappers.
 *
 * Subclasses (PoseTracker / FaceTracker / HandTracker) implement:
 *   - load()             : lazy-import MediaPipe + create the landmarker
 *   - start(videoEl)     : begin per-frame inference
 *   - stop()             : cancel the inference loop
 *   - getLandmark(name)  : friendly-name lookup
 *
 * The base class handles:
 *   - isAvailable / isLoading / latest state flags
 *   - subscribe / unsubscribe (`onUpdate(cb)`)
 *   - error isolation in subscribers (one bad callback can't kill the loop)
 *   - destroy() teardown
 */
export class BaseTracker {
  constructor() {
    this.isAvailable = false;
    this.isLoading = false;
    this.lastError = null;
    /** Most recent inference result. Subclass-specific shape. */
    this.latest = null;

    this._subscribers = [];
    this._destroyed = false;
  }

  // Subclasses override these.
  async load() { throw new Error('not implemented'); }
  async start(_videoElement) { throw new Error('not implemented'); }
  stop() { throw new Error('not implemented'); }

  /**
   * Subscribe to per-frame updates. Returns an unsubscribe function.
   */
  onUpdate(cb) {
    if (typeof cb !== 'function') return () => {};
    this._subscribers.push(cb);
    return () => {
      const i = this._subscribers.indexOf(cb);
      if (i !== -1) this._subscribers.splice(i, 1);
    };
  }

  /** Subclasses call this from their inference loop. */
  _emit(data) {
    this.latest = data;
    for (const cb of this._subscribers.slice()) {
      try {
        cb(data);
      } catch (err) {
        // Swallow subscriber errors so one bad callback doesn't kill
        // the whole tracking loop.
        if (typeof console !== 'undefined' && console.error) {
          console.error('[tracker subscriber error]', err);
        }
      }
    }
  }

  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;
    try { this.stop(); } catch (_) { /* ignore */ }
    this._subscribers = [];
    this.latest = null;
  }
}
